import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import Drawer from "react-modern-drawer";
import Select from "react-select";

import {
    fetchBillRetroLines,
    fetchBillRunDetail,
    postFinanceOpsInvoicePack,
} from "../../utils/apiUtils";
import { toWords } from "../../utils/commonUtils";
import { downloadFile } from "../../utils/downloadUtils";
import { openPrintDocument } from "../../utils/printDocument";
import { customStyles } from "../../utils/styleUtils";
import { FaDollarSign, FaEllipsisH, FaSlidersH } from "react-icons/fa";
import ApplyPaymentPanel from "./ApplyPaymentPanel";
import ApplyAdjustmentPanel from "./ApplyAdjustmentPanel";
import { refreshBillAfterPayment, refreshBillAfterAdjustment } from "./applyPaymentRefresh";
import "./BillRunDetail.scss";

const RETRO_PAGE_SIZE = 50;
const DRAWER_SIZE = 720;

const money = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });
};

const moneyInWords = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return null;
    try {
        const num = Number(n);
        const words = toWords.convert(Math.abs(num), { currency: true });
        if (!words) return null;
        if (num < 0) return `Negative ${words.charAt(0).toLowerCase()}${words.slice(1)}`;
        return words;
    } catch {
        return null;
    }
};

const moneyTipProps = (value) => {
    const tip = moneyInWords(value);
    if (!tip) return {};
    return { "data-tip": tip, title: tip };
};

/** Currency with hover tooltip spelling the amount in words. */
const MoneyAmount = ({ value, className = "num", as: Tag = "span" }) => {
    if (value == null || !Number.isFinite(Number(value))) {
        return <Tag className={className}>—</Tag>;
    }
    return (
        <Tag className={`${className} bill-run-detail__money`.trim()} {...moneyTipProps(value)}>
            {money(value)}
        </Tag>
    );
};

/** Signed amount: credits (negative) in red with a leading minus. */
const SignedAmount = ({ value }) => {
    if (value == null || !Number.isFinite(Number(value))) {
        return <span className="num">—</span>;
    }
    const n = Number(value);
    const tipProps = moneyTipProps(n);
    if (n === 0) {
        return (
            <span className="num bill-run-detail__money" {...tipProps}>
                {money(0)}
            </span>
        );
    }
    if (n < 0) {
        return (
            <span className="num bill-run-detail__adj bill-run-detail__money" {...tipProps}>
                -{money(Math.abs(n))}
            </span>
        );
    }
    return (
        <span className="num bill-run-detail__money" {...tipProps}>
            {money(n)}
        </span>
    );
};

const AdjustmentAmount = SignedAmount;

const fmtPeriod = (start, end) => {
    if (!start && !end) return "—";
    if (start && end) return `${start} → ${end}`;
    return start || end || "—";
};

const paidTone = (code) => {
    const c = String(code || "").toLowerCase();
    if (c === "paid" || c === "settled") return "ok";
    if (c === "partial") return "warn";
    if (c === "void" || c === "unpaid") return "danger";
    return "muted";
};

const hrpStatusTone = (label, code) => {
    const s = `${label || ""} ${code || ""}`.toLowerCase();
    if (s.includes("needs review") || /(^|\s)rv(\s|$)/.test(s)) return "warn";
    if (s.includes("reject") || /(^|\s)rj(\s|$)/.test(s)) return "danger";
    if (s.includes("accept") || /(^|\s)a(\s|$)/.test(s)) return "ok";
    return "";
};

const LAYOUT_OPTIONS = [
    { value: "lines", label: "Detail lines" },
    { value: "byPlan", label: "Breakdown by plan" },
    { value: "byCategory", label: "Breakdown by category" },
    { value: "summaryPlan", label: "Summary by plan" },
    { value: "summaryCategory", label: "Summary by category" },
];

const emptyTotals = () => ({
    currentCharges: 0,
    retroCharges: 0,
    totalAdjustments: 0,
    totalDue: 0,
});

const addSectionTotals = (acc, s) => {
    acc.currentCharges += Number(s.currentCharges) || 0;
    acc.retroCharges += Number(s.retroCharges) || 0;
    acc.totalAdjustments += Number(s.totalAdjustments) || 0;
    acc.totalDue += Number(s.totalDue) || 0;
    return acc;
};

const groupSections = (sections, mode) => {
    const byPlan = mode === "byPlan" || mode === "summaryPlan";
    const map = new Map();

    for (const s of sections) {
        const key = byPlan
            ? `${s.planKey ?? ""}|${s.planHccId || ""}|${s.planName || "Unassigned plan"}`
            : `${s.categoryKey ?? ""}|${s.categoryName || "Unassigned category"}`;

        if (!map.has(key)) {
            map.set(key, {
                key,
                title: byPlan ? s.planName || "Unassigned plan" : s.categoryName || "Unassigned category",
                subtitle: byPlan ? s.planHccId || null : null,
                lines: [],
                totals: emptyTotals(),
            });
        }
        const group = map.get(key);
        group.lines.push(s);
        addSectionTotals(group.totals, s);
    }

    return Array.from(map.values()).sort((a, b) =>
        String(a.title).localeCompare(String(b.title), undefined, { sensitivity: "base" }),
    );
};

/** Group adjustment / retro rows by the same Layout modes as charge sections. */
const groupMoneyLines = (rows, mode) => {
    const byPlan = mode === "byPlan" || mode === "summaryPlan";
    const map = new Map();

    for (const row of rows) {
        const planName = row.planName || "Unassigned plan";
        const planHccId = row.planHccId || null;
        const categoryName = row.categoryName || "Unassigned category";
        const key = byPlan
            ? `${planHccId || ""}|${planName}`
            : categoryName;

        if (!map.has(key)) {
            map.set(key, {
                key,
                title: byPlan ? planName : categoryName,
                subtitle: byPlan ? planHccId : null,
                lines: [],
                total: 0,
            });
        }
        const group = map.get(key);
        group.lines.push(row);
        group.total += Number(row.amount) || 0;
    }

    return Array.from(map.values()).sort((a, b) =>
        String(a.title).localeCompare(String(b.title), undefined, { sensitivity: "base" }),
    );
};

const isSummaryLayout = (mode) => mode === "summaryPlan" || mode === "summaryCategory";
const isGroupedLayout = (mode) =>
    mode === "byPlan" ||
    mode === "byCategory" ||
    mode === "summaryPlan" ||
    mode === "summaryCategory";

const AmountCells = ({ totals }) => (
    <>
        <td className="num"><MoneyAmount value={totals.currentCharges} /></td>
        <td className="num"><MoneyAmount value={totals.retroCharges} /></td>
        <td className="num">
            <AdjustmentAmount value={totals.totalAdjustments} />
        </td>
        <td className="num"><MoneyAmount value={totals.totalDue} /></td>
    </>
);

const AMOUNT_HEADERS = (
    <>
        <th className="num">Current charges</th>
        <th className="num">Retro charges</th>
        <th className="num">Adjustments</th>
        <th className="num">Amount due</th>
    </>
);

const FlatLinesTable = ({ sections, sectionDueTotal }) => {
    const totals = sections.reduce((acc, s) => addSectionTotals(acc, s), emptyTotals());
    return (
        <table className="bill-run-detail__table">
            <thead>
                <tr>
                    <th>Plan</th>
                    <th>Category</th>
                    {AMOUNT_HEADERS}
                </tr>
            </thead>
            <tbody>
                {sections.map((s) => (
                    <tr key={s.key}>
                        <td>
                            <div>{s.planName || "—"}</div>
                            {s.planHccId && <div className="bill-run-detail__muted">{s.planHccId}</div>}
                        </td>
                        <td>
                            {s.categoryName || "—"}
                            {s.isStatistical ? " (statistical)" : ""}
                        </td>
                        <td className="num"><MoneyAmount value={s.currentCharges} /></td>
                        <td className="num"><MoneyAmount value={s.retroCharges} /></td>
                        <td className="num">
                            <AdjustmentAmount value={s.totalAdjustments} />
                        </td>
                        <td className="num"><MoneyAmount value={s.totalDue} /></td>
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr>
                    <td colSpan={2}>Section total</td>
                    <td className="num"><MoneyAmount value={totals.currentCharges} /></td>
                    <td className="num"><MoneyAmount value={totals.retroCharges} /></td>
                    <td className="num">
                        <AdjustmentAmount value={totals.totalAdjustments} />
                    </td>
                    <td className="num"><MoneyAmount value={sectionDueTotal} /></td>
                </tr>
            </tfoot>
        </table>
    );
};

const GroupedDetailTable = ({ groups, groupBy, sectionDueTotal }) => {
    const detailLabel = groupBy === "plan" ? "Category" : "Plan";
    const totals = groups.reduce(
        (acc, g) => {
            acc.currentCharges += g.totals.currentCharges;
            acc.retroCharges += g.totals.retroCharges;
            acc.totalAdjustments += g.totals.totalAdjustments;
            acc.totalDue += g.totals.totalDue;
            return acc;
        },
        emptyTotals(),
    );
    return (
        <div className="bill-run-detail__groups">
            {groups.map((g) => (
                <div key={g.key} className="bill-run-detail__group">
                    <div className="bill-run-detail__group-head">
                        <div>
                            <h3>{g.title}</h3>
                            {g.subtitle && <p className="bill-run-detail__muted">{g.subtitle}</p>}
                        </div>
                        <MoneyAmount value={g.totals.totalDue} as="strong" className="bill-run-detail__group-due" />
                    </div>
                    <table className="bill-run-detail__table">
                        <thead>
                            <tr>
                                <th>{detailLabel}</th>
                                {AMOUNT_HEADERS}
                            </tr>
                        </thead>
                        <tbody>
                            {g.lines.map((s) => (
                                <tr key={s.key}>
                                    <td>
                                        {groupBy === "plan" ? (
                                            <>
                                                {s.categoryName || "—"}
                                                {s.isStatistical ? " (statistical)" : ""}
                                            </>
                                        ) : (
                                            <>
                                                <div>{s.planName || "—"}</div>
                                                {s.planHccId && (
                                                    <div className="bill-run-detail__muted">{s.planHccId}</div>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className="num"><MoneyAmount value={s.currentCharges} /></td>
                                    <td className="num"><MoneyAmount value={s.retroCharges} /></td>
                                    <td className="num">
                                        <AdjustmentAmount value={s.totalAdjustments} />
                                    </td>
                                    <td className="num"><MoneyAmount value={s.totalDue} /></td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>Subtotal</td>
                                <AmountCells totals={g.totals} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            ))}
            <table className="bill-run-detail__table bill-run-detail__table--grand">
                <colgroup>
                    <col />
                    <col style={{ width: "7.5rem" }} />
                    <col style={{ width: "7.5rem" }} />
                    <col style={{ width: "7.5rem" }} />
                    <col style={{ width: "7.5rem" }} />
                </colgroup>
                <tbody>
                    <tr>
                        <td>Grand total</td>
                        <td className="num"><MoneyAmount value={totals.currentCharges} /></td>
                        <td className="num"><MoneyAmount value={totals.retroCharges} /></td>
                        <td className="num">
                            <AdjustmentAmount value={totals.totalAdjustments} />
                        </td>
                        <td className="num"><MoneyAmount value={sectionDueTotal} /></td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const SummaryTable = ({ groups, label, sectionDueTotal }) => {
    const totals = groups.reduce(
        (acc, g) => {
            acc.currentCharges += g.totals.currentCharges;
            acc.retroCharges += g.totals.retroCharges;
            acc.totalAdjustments += g.totals.totalAdjustments;
            acc.totalDue += g.totals.totalDue;
            return acc;
        },
        emptyTotals(),
    );
    return (
        <table className="bill-run-detail__table">
            <thead>
                <tr>
                    <th>{label}</th>
                    {AMOUNT_HEADERS}
                </tr>
            </thead>
            <tbody>
                {groups.map((g) => (
                    <tr key={g.key}>
                        <td>
                            <div>{g.title}</div>
                            {g.subtitle && <div className="bill-run-detail__muted">{g.subtitle}</div>}
                            <div className="bill-run-detail__muted">
                                {g.lines.length} line{g.lines.length === 1 ? "" : "s"}
                            </div>
                        </td>
                        <AmountCells totals={g.totals} />
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr>
                    <td>Total</td>
                    <td className="num"><MoneyAmount value={totals.currentCharges} /></td>
                    <td className="num"><MoneyAmount value={totals.retroCharges} /></td>
                    <td className="num">
                        <AdjustmentAmount value={totals.totalAdjustments} />
                    </td>
                    <td className="num"><MoneyAmount value={sectionDueTotal} /></td>
                </tr>
            </tfoot>
        </table>
    );
};

const DetailField = ({ label, children }) => (
    <div className="bill-run-detail__panel-field">
        <dt>{label}</dt>
        <dd>{children ?? "—"}</dd>
    </div>
);

const BillRunDetail = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { billFactKey } = useParams();
    const [searchParams] = useSearchParams();
    const asOf = searchParams.get("asOf") || undefined;
    const [layout, setLayout] = useState(LAYOUT_OPTIONS[1]);
    /** @type {[{ type: 'guide'|'adjustment'|'retro', data?: object } | null, Function]} */
    const [panel, setPanel] = useState(null);
    const [showMoreActions, setShowMoreActions] = useState(false);
    const moreActionsRef = useRef(null);

    useEffect(() => {
        if (!showMoreActions) return undefined;
        const onDocMouseDown = (event) => {
            if (!moreActionsRef.current?.contains(event.target)) {
                setShowMoreActions(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") setShowMoreActions(false);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onDocMouseDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [showMoreActions]);

    const { data, isLoading, error } = useQuery({
        queryKey: ["bill-run-detail", billFactKey, asOf || null],
        queryFn: () => fetchBillRunDetail({ billFactKey, asOfDate: asOf }),
        enabled: !!billFactKey,
        staleTime: 30_000,
        retry: false,
    });

    const retroFilters = panel?.type === "retro" ? panel.data : null;
    const [retroScrollRoot, setRetroScrollRoot] = useState(null);
    const { ref: retroLoadMoreRef, inView: retroLoadMoreInView } = useInView({
        root: retroScrollRoot,
        rootMargin: "120px",
        threshold: 0,
    });

    const {
        data: retroLinesData,
        isLoading: retroLinesLoading,
        isFetchingNextPage: retroFetchingNext,
        fetchNextPage: fetchNextRetroPage,
        hasNextPage: hasMoreRetroLines,
        error: retroLinesError,
    } = useInfiniteQuery({
        queryKey: [
            "bill-retro-lines",
            billFactKey,
            retroFilters?.reason || null,
            retroFilters?.categoryName || null,
            retroFilters?.planName || null,
            retroFilters?.planHccId || null,
            retroFilters?.periodStart || null,
            retroFilters?.periodEnd || null,
        ],
        queryFn: ({ pageParam = 0 }) =>
            fetchBillRetroLines({
                billFactKey,
                reason: retroFilters.reason,
                categoryName: retroFilters.categoryName || undefined,
                planName: retroFilters.planName || undefined,
                planHccId: retroFilters.planHccId || undefined,
                periodStart: retroFilters.periodStart || undefined,
                periodEnd: retroFilters.periodEnd || undefined,
                limit: RETRO_PAGE_SIZE,
                offset: pageParam,
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const loaded = pages.reduce((sum, p) => sum + (p?.lines?.length || 0), 0);
            const total = lastPage?.total ?? 0;
            if (loaded >= total) return undefined;
            if ((lastPage?.lines?.length || 0) < RETRO_PAGE_SIZE) return undefined;
            return loaded;
        },
        enabled: !!billFactKey && !!retroFilters,
        staleTime: 30_000,
        retry: false,
    });

    useEffect(() => {
        if (retroLoadMoreInView && hasMoreRetroLines && !retroFetchingNext) {
            fetchNextRetroPage();
        }
    }, [retroLoadMoreInView, hasMoreRetroLines, retroFetchingNext, fetchNextRetroPage]);

    const bill = data?.bill;
    const sections = data?.sections || [];
    const charges = data?.currentCharges || [];
    const payments = data?.payments || [];
    const adjustments = data?.adjustments || [];
    const reviewTriggers = data?.reviewTriggers || [];
    const retroBreakdown = data?.retroBreakdown || [];
    const invoiceTotals = data?.invoiceTotals;

    const sectionDueTotal = useMemo(
        () => sections.reduce((sum, s) => sum + (Number(s.totalDue) || 0), 0),
        [sections],
    );

    const paymentsAllocatedTotal = useMemo(
        () => payments.reduce((sum, p) => sum + (Number(p.allocatedAmount) || 0), 0),
        [payments],
    );

    const adjustmentsTotal = useMemo(
        () => adjustments.reduce((sum, a) => sum + (Number(a.amount) || 0), 0),
        [adjustments],
    );

    const retroTotal = useMemo(
        () => retroBreakdown.reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
        [retroBreakdown],
    );

    const sectionRetroTotal = useMemo(
        () => sections.reduce((sum, s) => sum + (Number(s.retroCharges) || 0), 0),
        [sections],
    );

    const sectionAdjTotal = useMemo(
        () => sections.reduce((sum, s) => sum + (Number(s.totalAdjustments) || 0), 0),
        [sections],
    );

    const paymentStatusLabel = (p) => {
        if (p?.isVoid) return "Void";
        if (p?.isReversal) return "Reversal";
        if (p?.isReconciled) return "Reconciled";
        return "Applied";
    };

    const closePanel = () => setPanel(null);
    const openGuide = () => setPanel({ type: "guide" });
    const openApplyPayment = () => setPanel({ type: "apply-payment" });
    const openApplyAdjustment = () => setPanel({ type: "apply-adjustment" });
    const openAdjustment = (a) => setPanel({ type: "adjustment", data: a });
    const openRetro = (r) => setPanel({ type: "retro", data: r });
    const isApplyPanel =
        panel?.type === "apply-payment" || panel?.type === "apply-adjustment";
    const panelKicker =
        panel?.type === "guide"
            ? "Billing guide"
            : panel?.type === "apply-payment" || panel?.type === "apply-adjustment"
              ? "Submit to HRP"
              : panel?.type === "adjustment"
                ? "Billing adjustment"
                : "Retro detail";
    const panelTitle =
        panel?.type === "guide"
            ? "How billing works"
            : panel?.type === "apply-payment"
              ? "Apply payment"
              : panel?.type === "apply-adjustment"
                ? "Apply adjustment"
                : panel?.type === "adjustment"
                  ? panel.data?.reason || "Adjustment"
                  : panel?.data?.reason || "Retro charges";

    const fundingRequestsHref = bill?.accountKey
        ? `/finance/funding-requests?accountKey=${encodeURIComponent(bill.accountKey)}${
              bill.accountHccId
                  ? `&accountHccId=${encodeURIComponent(bill.accountHccId)}`
                  : ""
          }${
              bill.accountName
                  ? `&accountName=${encodeURIComponent(bill.accountName)}`
                  : ""
          }`
        : "/finance/funding-requests";

    const retroLines = useMemo(
        () => (retroLinesData?.pages || []).flatMap((p) => p?.lines || []),
        [retroLinesData],
    );
    const retroLinesTotal = Number(retroLinesData?.pages?.[0]?.total) || 0;
    const retroLinesAmount = useMemo(
        () => retroLines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0),
        [retroLines],
    );

    const layoutValue = layout?.value || "byPlan";

    const planGroups = useMemo(
        () => groupSections(sections, "byPlan"),
        [sections],
    );
    const categoryGroups = useMemo(
        () => groupSections(sections, "byCategory"),
        [sections],
    );

    const adjustmentGroups = useMemo(
        () => (isGroupedLayout(layoutValue) ? groupMoneyLines(adjustments, layoutValue) : null),
        [adjustments, layoutValue],
    );
    const retroGroups = useMemo(
        () => (isGroupedLayout(layoutValue) ? groupMoneyLines(retroBreakdown, layoutValue) : null),
        [retroBreakdown, layoutValue],
    );

    const onPrint = async () => {
        const root = document.getElementById("bill-invoice");
        if (!root) return;
        try {
            await openPrintDocument(root, `Invoice ${bill?.key || billFactKey || ""}`.trim());
        } catch (err) {
            console.error(err);
            window.alert(err?.message || "Unable to open print preview.");
        }
    };

    const invoicePackMutation = useMutation({
        mutationFn: async () => {
            if (!data?.bill) throw new Error("Bill detail not loaded");
            return postFinanceOpsInvoicePack({
                billFactKey: data.bill.key ?? billFactKey,
                asOfDate: data.asOfDate || asOf,
                detail: data,
            });
        },
        onSuccess: (result) => {
            const pack = result?.pack;
            if (!pack?.zipBase64 || !pack?.zipFileName) {
                throw new Error("Invoice pack zip missing from response");
            }
            const binary = atob(pack.zipBase64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            downloadFile({
                data: bytes,
                fileName: pack.zipFileName,
                fileType: pack.zipContentType || "application/zip",
            });
        },
        onError: (err) => {
            window.alert(err?.message || "Unable to build invoice pack.");
        },
    });

    const onSendInvoice = () => {
        if (!bill) return;
        const account = bill.accountName || bill.accountHccId || "Account";
        const period = fmtPeriod(bill.periodStart, bill.periodEnd);
        const subject = `Invoice ${bill.key} — ${account} (${period})`;
        const body = [
            `Invoice / Bill #: ${bill.key}`,
            `Account: ${account}${bill.accountHccId ? ` (${bill.accountHccId})` : ""}`,
            `Billing period: ${period}`,
            `Billing cycle: ${bill.billingCycleName || "—"}`,
            `Due date: ${bill.dueDate || "—"}`,
            `Total due: ${money(bill.totalDue)}`,
            `Total charges: ${money(bill.totalCharges)}`,
            `Adjustments: ${money(bill.totalAdjustments)}`,
            `Payments applied: ${money(bill.totalPayments)}`,
            `Paid status: ${bill.paidStatus || "—"}`,
            "",
            "Please review the attached invoice (Save as PDF from Print) and remit payment.",
            "",
            `View in Centivo Business: ${window.location.href}`,
        ].join("\n");

        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    const renderLineItems = () => {
        if (!sections.length) {
            return <p className="bill-run-detail__muted">No section lines on this bill.</p>;
        }
        if (layoutValue === "lines") {
            return <FlatLinesTable sections={sections} sectionDueTotal={sectionDueTotal} />;
        }
        if (layoutValue === "byPlan") {
            return (
                <GroupedDetailTable
                    groups={planGroups}
                    groupBy="plan"
                    sectionDueTotal={sectionDueTotal}
                />
            );
        }
        if (layoutValue === "byCategory") {
            return (
                <GroupedDetailTable
                    groups={categoryGroups}
                    groupBy="category"
                    sectionDueTotal={sectionDueTotal}
                />
            );
        }
        if (layoutValue === "summaryPlan") {
            return (
                <SummaryTable
                    groups={planGroups}
                    label="Plan"
                    sectionDueTotal={sectionDueTotal}
                />
            );
        }
        return (
            <SummaryTable
                groups={categoryGroups}
                label="Category"
                sectionDueTotal={sectionDueTotal}
            />
        );
    };

    const renderAdjustmentRows = (rows) =>
        rows.map((a) => (
            <tr
                key={a.key}
                className="bill-run-detail__click-row"
                onClick={() => openAdjustment(a)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openAdjustment(a);
                    }
                }}
                tabIndex={0}
                role="button"
            >
                <td>
                    <span className="bill-run-detail__method">{a.reason || "—"}</span>
                    {a.reasonCode ? (
                        <div className="bill-run-detail__muted">Code {a.reasonCode}</div>
                    ) : null}
                    {a.enteredBy ? (
                        <div className="bill-run-detail__muted">By {a.enteredBy}</div>
                    ) : null}
                </td>
                <td>
                    <div>{a.categoryName || "—"}</div>
                    <div className="bill-run-detail__muted">{a.planName || a.planHccId || "—"}</div>
                </td>
                <td>
                    <div>{a.comment || "—"}</div>
                    {a.periodStart || a.periodEnd ? (
                        <div className="bill-run-detail__muted">
                            Period {fmtPeriod(a.periodStart, a.periodEnd)}
                        </div>
                    ) : null}
                </td>
                <td>{a.adjustmentDate || a.coverageDate || "—"}</td>
                <td className="num">
                    <SignedAmount value={a.amount} />
                </td>
            </tr>
        ));

    const renderRetroRows = (rows) =>
        rows.map((r, i) => (
            <tr
                key={`${r.reason}-${r.categoryName}-${r.planName}-${r.periodStart}-${i}`}
                className="bill-run-detail__click-row"
                onClick={() => openRetro(r)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openRetro(r);
                    }
                }}
                tabIndex={0}
                role="button"
            >
                <td>
                    <span className="bill-run-detail__method">{r.reason || "—"}</span>
                </td>
                <td>{r.categoryName || "—"}</td>
                <td>
                    <div>{r.planName || "—"}</div>
                    {r.planHccId ? (
                        <div className="bill-run-detail__muted">{r.planHccId}</div>
                    ) : null}
                </td>
                <td>{fmtPeriod(r.periodStart, r.periodEnd)}</td>
                <td className="num">{r.lineCount ?? "—"}</td>
                <td className="num">
                    <SignedAmount value={r.amount} />
                </td>
            </tr>
        ));

    const renderAdjustmentsBlock = () => {
        if (!adjustments.length) return null;
        const groupLabel =
            layoutValue === "byPlan" || layoutValue === "summaryPlan" ? "Plan" : "Category";

        if (isSummaryLayout(layoutValue) && adjustmentGroups) {
            return (
                <>
                    <h3 className="bill-run-detail__subhead">
                        Billing adjustments
                        <span className="bill-run-detail__muted"> — summary by {groupLabel.toLowerCase()}</span>
                    </h3>
                    <table className="bill-run-detail__table bill-run-detail__table--payments">
                        <thead>
                            <tr>
                                <th>{groupLabel}</th>
                                <th className="num">Lines</th>
                                <th className="num">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {adjustmentGroups.map((g) => (
                                <tr key={g.key}>
                                    <td>
                                        <div>{g.title}</div>
                                        {g.subtitle ? (
                                            <div className="bill-run-detail__muted">{g.subtitle}</div>
                                        ) : null}
                                    </td>
                                    <td className="num">{g.lines.length}</td>
                                    <td className="num">
                                        <SignedAmount value={g.total} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={2}>Adjustments total</td>
                                <td className="num">
                                    <SignedAmount value={adjustmentsTotal} />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </>
            );
        }

        if (isGroupedLayout(layoutValue) && adjustmentGroups) {
            return (
                <>
                    <h3 className="bill-run-detail__subhead">
                        Billing adjustments
                        <span className="bill-run-detail__muted">
                            {" "}
                            — by {groupLabel.toLowerCase()}, click a row for details
                        </span>
                    </h3>
                    <div className="bill-run-detail__groups">
                        {adjustmentGroups.map((g) => (
                            <div key={g.key} className="bill-run-detail__group">
                                <div className="bill-run-detail__group-head">
                                    <div>
                                        <h3>{g.title}</h3>
                                        {g.subtitle ? (
                                            <p className="bill-run-detail__muted">{g.subtitle}</p>
                                        ) : null}
                                    </div>
                                    <strong className="bill-run-detail__group-due">
                                        <SignedAmount value={g.total} />
                                    </strong>
                                </div>
                                <table className="bill-run-detail__table bill-run-detail__table--payments">
                                    <thead>
                                        <tr>
                                            <th>Reason</th>
                                            <th>Category / plan</th>
                                            <th>Comment</th>
                                            <th>Date</th>
                                            <th className="num">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>{renderAdjustmentRows(g.lines)}</tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={4}>Subtotal</td>
                                            <td className="num">
                                                <SignedAmount value={g.total} />
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        ))}
                    </div>
                    <table className="bill-run-detail__table bill-run-detail__table--grand">
                        <tbody>
                            <tr>
                                <td>Adjustments total</td>
                                <td className="num">
                                    <SignedAmount value={adjustmentsTotal} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </>
            );
        }

        return (
            <>
                <h3 className="bill-run-detail__subhead">
                    Billing adjustments
                    <span className="bill-run-detail__muted"> — click a row for details</span>
                </h3>
                <table className="bill-run-detail__table bill-run-detail__table--payments">
                    <thead>
                        <tr>
                            <th>Reason</th>
                            <th>Category / plan</th>
                            <th>Comment</th>
                            <th>Date</th>
                            <th className="num">Amount</th>
                        </tr>
                    </thead>
                    <tbody>{renderAdjustmentRows(adjustments)}</tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={4}>Adjustments total</td>
                            <td className="num">
                                <SignedAmount value={adjustmentsTotal} />
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </>
        );
    };

    const renderRetroBlock = () => {
        if (!retroBreakdown.length) return null;
        const groupLabel =
            layoutValue === "byPlan" || layoutValue === "summaryPlan" ? "Plan" : "Category";

        if (isSummaryLayout(layoutValue) && retroGroups) {
            return (
                <>
                    <h3 className="bill-run-detail__subhead">
                        Retroactive charges
                        <span className="bill-run-detail__muted"> — summary by {groupLabel.toLowerCase()}</span>
                    </h3>
                    <table className="bill-run-detail__table bill-run-detail__table--payments">
                        <thead>
                            <tr>
                                <th>{groupLabel}</th>
                                <th className="num">Rows</th>
                                <th className="num">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {retroGroups.map((g) => (
                                <tr key={g.key}>
                                    <td>
                                        <div>{g.title}</div>
                                        {g.subtitle ? (
                                            <div className="bill-run-detail__muted">{g.subtitle}</div>
                                        ) : null}
                                    </td>
                                    <td className="num">{g.lines.length}</td>
                                    <td className="num">
                                        <SignedAmount value={g.total} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={2}>Retro total</td>
                                <td className="num">
                                    <SignedAmount value={retroTotal} />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </>
            );
        }

        if (isGroupedLayout(layoutValue) && retroGroups) {
            return (
                <>
                    <h3 className="bill-run-detail__subhead">
                        Retroactive charges
                        <span className="bill-run-detail__muted">
                            {" "}
                            — by {groupLabel.toLowerCase()}, click a row to see members
                        </span>
                    </h3>
                    <div className="bill-run-detail__groups">
                        {retroGroups.map((g) => (
                            <div key={g.key} className="bill-run-detail__group">
                                <div className="bill-run-detail__group-head">
                                    <div>
                                        <h3>{g.title}</h3>
                                        {g.subtitle ? (
                                            <p className="bill-run-detail__muted">{g.subtitle}</p>
                                        ) : null}
                                    </div>
                                    <strong className="bill-run-detail__group-due">
                                        <SignedAmount value={g.total} />
                                    </strong>
                                </div>
                                <table className="bill-run-detail__table bill-run-detail__table--payments">
                                    <thead>
                                        <tr>
                                            <th>Reason</th>
                                            <th>Category</th>
                                            <th>Plan</th>
                                            <th>Period</th>
                                            <th className="num">Lines</th>
                                            <th className="num">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>{renderRetroRows(g.lines)}</tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={5}>Subtotal</td>
                                            <td className="num">
                                                <SignedAmount value={g.total} />
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        ))}
                    </div>
                    <table className="bill-run-detail__table bill-run-detail__table--grand">
                        <tbody>
                            <tr>
                                <td>Retro total</td>
                                <td className="num">
                                    <SignedAmount value={retroTotal} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </>
            );
        }

        return (
            <>
                <h3 className="bill-run-detail__subhead">
                    Retroactive charges
                    <span className="bill-run-detail__muted"> — click a row to see members</span>
                </h3>
                <table className="bill-run-detail__table bill-run-detail__table--payments">
                    <thead>
                        <tr>
                            <th>Reason</th>
                            <th>Category</th>
                            <th>Plan</th>
                            <th>Period</th>
                            <th className="num">Lines</th>
                            <th className="num">Amount</th>
                        </tr>
                    </thead>
                    <tbody>{renderRetroRows(retroBreakdown)}</tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={5}>Retro total</td>
                            <td className="num">
                                <SignedAmount value={retroTotal} />
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </>
        );
    };

    if (isLoading) {
        return (
            <div className="container bill-run-detail scrollable-form">
                <p className="muted">Loading invoice…</p>
            </div>
        );
    }

    if (error || data?.error || !bill) {
        return (
            <div className="container bill-run-detail scrollable-form">
                <p className="danger">{error?.message || data?.error || "Bill not found"}</p>
                <button type="button" className="bill-run-detail__link" onClick={() => navigate("/finance/bills")}>
                    Back to Bills
                </button>
            </div>
        );
    }

    const hrpTone = hrpStatusTone(bill.statusLabel, bill.status);
    const needsReview = /needs review/i.test(String(bill.statusLabel || ""));

    return (
        <div className="container bill-run-detail scrollable-form">
            <div className="bill-run-detail__toolbar no-print">
                <div className="bill-run-detail__toolbar-left">
                    <div className="bill-run-detail__heading">
                        <p className="title">Invoice {bill.key}</p>
                        <p className="sub-title">
                            {bill.accountName || bill.accountHccId || "Account"}
                            {bill.accountHccId ? ` · ${bill.accountHccId}` : ""}
                            {" · "}
                            {fmtPeriod(bill.periodStart, bill.periodEnd)}
                        </p>
                        <button
                            type="button"
                            className="bill-run-detail__link"
                            onClick={() => (panel?.type === "guide" ? closePanel() : openGuide())}
                            aria-pressed={panel?.type === "guide"}
                        >
                            How billing works
                        </button>
                    </div>
                    <div className="bill-run-detail__layout-field">
                        <label htmlFor="invoice-layout">Layout</label>
                        <Select
                            inputId="invoice-layout"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            options={LAYOUT_OPTIONS}
                            value={layout}
                            onChange={(opt) => setLayout(opt || LAYOUT_OPTIONS[1])}
                            isSearchable={false}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            styles={{
                                ...customStyles,
                                menuPortal: (base) => ({ ...base, zIndex: 200 }),
                            }}
                        />
                    </div>
                </div>
                <div className="bill-run-detail__toolbar-actions">
                    <button
                        type="button"
                        className="btn bill-run-detail__btn bill-run-detail__btn--apply"
                        disabled={!bill || needsReview}
                        title={
                            needsReview
                                ? "Disabled while bill is Needs Review in HRP — accept the bill first"
                                : undefined
                        }
                        onClick={() =>
                            panel?.type === "apply-payment" ? closePanel() : openApplyPayment()
                        }
                        aria-pressed={panel?.type === "apply-payment"}
                    >
                        <FaDollarSign aria-hidden />
                        Apply payment
                    </button>
                    <button
                        type="button"
                        className="btn bill-run-detail__btn bill-run-detail__btn--adjust"
                        disabled={!bill || needsReview}
                        title={
                            needsReview
                                ? "Disabled while bill is Needs Review in HRP — accept the bill first"
                                : undefined
                        }
                        onClick={() =>
                            panel?.type === "apply-adjustment"
                                ? closePanel()
                                : openApplyAdjustment()
                        }
                        aria-pressed={panel?.type === "apply-adjustment"}
                    >
                        <FaSlidersH aria-hidden />
                        Apply adjustment
                    </button>
                    <div className="bill-run-detail__more" ref={moreActionsRef}>
                        <button
                            type="button"
                            className="bill-run-detail__more-trigger"
                            onClick={() => setShowMoreActions((v) => !v)}
                            aria-haspopup="menu"
                            aria-expanded={showMoreActions}
                            aria-label="More actions"
                            title="More actions"
                        >
                            <FaEllipsisH aria-hidden />
                        </button>
                        {showMoreActions ? (
                            <div className="bill-run-detail__more-menu" role="menu">
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="bill-run-detail__more-item"
                                    onClick={() => {
                                        setShowMoreActions(false);
                                        onPrint();
                                    }}
                                >
                                    Print / Save PDF
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="bill-run-detail__more-item"
                                    disabled={!bill || invoicePackMutation.isPending}
                                    onClick={() => {
                                        setShowMoreActions(false);
                                        invoicePackMutation.mutate();
                                    }}
                                >
                                    {invoicePackMutation.isPending
                                        ? "Building pack…"
                                        : "Download invoice pack"}
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="bill-run-detail__more-item"
                                    onClick={() => {
                                        setShowMoreActions(false);
                                        onSendInvoice();
                                    }}
                                >
                                    Send invoice
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {needsReview ? (
                <div className="bill-run-detail__needs-review no-print" role="status">
                    <p>
                        This bill is <strong>Needs Review</strong> in HRP, so Apply payment
                        / adjustment is disabled. Accept the bill in HealthRules Manager first —
                        payments will not allocate to Needs Review bills.
                    </p>
                    {reviewTriggers.length ? (
                        <ul className="bill-run-detail__review-triggers">
                            {reviewTriggers.map((t) => (
                                <li key={t.key || `${t.code}-${t.policyName}`}>
                                    <strong>{t.description || t.policyName || "Review trigger"}</strong>
                                    {t.policyName && t.description !== t.policyName ? (
                                        <span className="bill-run-detail__muted">
                                            {" "}
                                            · {t.policyName}
                                        </span>
                                    ) : null}
                                    {t.domain ? (
                                        <span className="bill-run-detail__muted">
                                            {" "}
                                            · {t.domain}
                                        </span>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="bill-run-detail__muted">
                            No review-trigger details found in the warehouse yet.
                        </p>
                    )}
                </div>
            ) : null}

            <article className="bill-run-detail__invoice" id="bill-invoice">
                <header className="bill-run-detail__invoice-head">
                    <div>
                        <img
                            className="bill-run-detail__logo"
                            src="https://centivo.com/wp-content/themes/studio-simpatico/svgs/logo.svg"
                            alt="Centivo"
                        />
                        <h1 className="bill-run-detail__invoice-title">Invoice</h1>
                        <p className="bill-run-detail__muted">
                            HRP bill #{bill.key}
                        </p>
                    </div>
                    <div className="bill-run-detail__invoice-meta">
                        <div>
                            <span className="bill-run-detail__meta-label">Bill #</span>
                            <strong>{bill.key}</strong>
                        </div>
                        <div>
                            <span className="bill-run-detail__meta-label">Paid status</span>
                            <span className={`bill-run-detail__status bill-run-detail__status--${paidTone(bill.paidStatusCode)}`}>
                                {bill.paidStatus || "—"}
                            </span>
                        </div>
                        <div>
                            <span className="bill-run-detail__meta-label">HRP status</span>
                            <span
                                className={`bill-run-detail__status${
                                    hrpTone ? ` bill-run-detail__status--${hrpTone}` : ""
                                }`}
                            >
                                {bill.statusLabel || bill.status || "—"}
                            </span>
                        </div>
                        <div>
                            <span className="bill-run-detail__meta-label">Due date</span>
                            <strong>{bill.dueDate || "—"}</strong>
                        </div>
                    </div>
                </header>

                <section className="bill-run-detail__parties">
                    <div>
                        <h2>Bill to</h2>
                        <p className="bill-run-detail__party-name">{bill.accountName || "—"}</p>
                        {bill.accountHccId && (
                            <p className="bill-run-detail__muted">Account ID: {bill.accountHccId}</p>
                        )}
                        {bill.billedPartyType && (
                            <p className="bill-run-detail__muted">Party: {bill.billedPartyType}</p>
                        )}
                    </div>
                    <div>
                        <h2>Billing period</h2>
                        <p>{fmtPeriod(bill.periodStart, bill.periodEnd)}</p>
                        <p className="bill-run-detail__muted">Cycle: {bill.billingCycleName || "—"}</p>
                        {bill.processingDate && (
                            <p className="bill-run-detail__muted">Processed: {bill.processingDate}</p>
                        )}
                        {bill.sentDate && (
                            <p className="bill-run-detail__muted">Sent: {bill.sentDate}</p>
                        )}
                        {!!bill.contents?.length && (
                            <p className="bill-run-detail__muted">
                                Contents: {bill.contents.join(" · ")}
                            </p>
                        )}
                    </div>
                </section>

                <section className="bill-run-detail__summary-cards">
                    <div>
                        <span className="bill-run-detail__meta-label">Total charges</span>
                        <MoneyAmount value={bill.totalCharges} as="strong" className="" />
                    </div>
                    <div>
                        <span className="bill-run-detail__meta-label">Adjustments</span>
                        <strong>
                            <AdjustmentAmount value={bill.totalAdjustments} />
                        </strong>
                    </div>
                    <div>
                        <span className="bill-run-detail__meta-label">Payments</span>
                        <MoneyAmount value={bill.totalPayments} as="strong" className="" />
                    </div>
                    <div className="bill-run-detail__summary-due">
                        <span className="bill-run-detail__meta-label">Amount due</span>
                        <MoneyAmount value={bill.totalDue} as="strong" className="" />
                    </div>
                </section>

                <section className="bill-run-detail__section">
                    <div className="bill-run-detail__section-head">
                        <h2>Invoice line items</h2>
                        <span className="bill-run-detail__layout-badge">{layout?.label}</span>
                    </div>
                    {renderLineItems()}
                </section>

                {!!charges.length && layoutValue === "lines" && (
                    <section className="bill-run-detail__section">
                        <h2>Aggregated current charges</h2>
                        <table className="bill-run-detail__table">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Plan</th>
                                    <th>Period</th>
                                    <th>Method</th>
                                    <th className="num">Rate</th>
                                    <th className="num">Count</th>
                                    <th className="num">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {charges.map((c, i) => (
                                    <tr key={`${c.billingCategory}-${c.benefitPlan}-${i}`}>
                                        <td>{c.billingCategory || "—"}</td>
                                        <td>{c.benefitPlan || "—"}</td>
                                        <td>{fmtPeriod(c.billedPeriodStartDate, c.billedPeriodEndDate)}</td>
                                        <td>{c.billingMethod || "—"}</td>
                                        <td className="num"><MoneyAmount value={c.rate} /></td>
                                        <td className="num">{c.count != null ? c.count : "—"}</td>
                                        <td className="num"><MoneyAmount value={c.totalBilledAmount} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {(!!adjustments.length || !!retroBreakdown.length || sectionRetroTotal !== 0 || sectionAdjTotal !== 0) && (
                    <section className="bill-run-detail__section">
                        <div className="bill-run-detail__section-head">
                            <h2>Adjustments / retro</h2>
                            <span className="bill-run-detail__layout-badge">
                                {layout?.label}
                                {" · "}
                                Retro{" "}
                                <MoneyAmount
                                    value={invoiceTotals?.totalRetroCharges ?? sectionRetroTotal}
                                    className=""
                                />
                                {" · "}
                                Adj{" "}
                                <SignedAmount
                                    value={
                                        invoiceTotals?.totalAdjustments ??
                                        bill.totalAdjustments ??
                                        sectionAdjTotal
                                    }
                                />
                            </span>
                        </div>

                        {renderAdjustmentsBlock()}
                        {renderRetroBlock()}

                        {!adjustments.length && !retroBreakdown.length && (
                            <p className="bill-run-detail__muted">
                                Section totals include retro/adjustments, but no line-level detail was found.
                            </p>
                        )}
                    </section>
                )}

                <section className="bill-run-detail__section">
                    <h2>Payments</h2>
                    {!payments.length ? (
                        <p className="bill-run-detail__muted">No payments allocated to this bill.</p>
                    ) : (
                        <table className="bill-run-detail__table bill-run-detail__table--payments">
                            <thead>
                                <tr>
                                    <th>Method</th>
                                    <th>Reference</th>
                                    <th>Date</th>
                                    <th>Received</th>
                                    <th className="num">Amount</th>
                                    <th className="num">Allocated</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((p) => {
                                    const numericKey = Number(p.key);
                                    const paymentHref =
                                        Number.isFinite(numericKey) && !String(p.key).startsWith("local-")
                                            ? `/finance/premium-payments/${numericKey}${
                                                  asOf ? `?asOf=${encodeURIComponent(asOf)}` : ""
                                              }`
                                            : null;
                                    return (
                                    <tr key={p.key}>
                                        <td>
                                            <span className="bill-run-detail__method">
                                                {p.paymentType || "—"}
                                            </span>
                                            <div className="bill-run-detail__muted">
                                                {paymentHref ? (
                                                    <Link to={paymentHref}>ID {p.key}</Link>
                                                ) : (
                                                    <>ID {p.key}</>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                {paymentHref ? (
                                                    <Link to={paymentHref}>
                                                        {p.reference || p.paymentNumber || "—"}
                                                    </Link>
                                                ) : (
                                                    p.reference || p.paymentNumber || "—"
                                                )}
                                            </div>
                                            {p.transactNum && p.transactNum !== String(p.paymentNumber || "") && (
                                                <div className="bill-run-detail__muted">
                                                    Txn {p.transactNum}
                                                </div>
                                            )}
                                        </td>
                                        <td>{p.paymentDate || "—"}</td>
                                        <td>{p.receiptDate || "—"}</td>
                                        <td className="num"><MoneyAmount value={p.amount} /></td>
                                        <td className="num"><MoneyAmount value={p.allocatedAmount} /></td>
                                        <td>
                                            <span
                                                className={`bill-run-detail__status bill-run-detail__status--${
                                                    p.isVoid || p.isReversal ? "danger" : p.isReconciled ? "ok" : "warn"
                                                }`}
                                            >
                                                {paymentStatusLabel(p)}
                                            </span>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={5}>Allocated total</td>
                                    <td className="num"><MoneyAmount value={paymentsAllocatedTotal} /></td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </section>

                <section className="bill-run-detail__section bill-run-detail__totals-box">
                    <h2>Invoice totals</h2>
                    <dl className="bill-run-detail__totals">
                        <div>
                            <dt>Current charges</dt>
                            <dd><MoneyAmount value={invoiceTotals?.totalCurrentCharges ?? bill.totalCharges} className="" /></dd>
                        </div>
                        <div>
                            <dt>Retro charges</dt>
                            <dd>
                                <SignedAmount
                                    value={invoiceTotals?.totalRetroCharges ?? sectionRetroTotal}
                                />
                            </dd>
                        </div>
                        <div>
                            <dt>Adjustments</dt>
                            <dd>
                                <SignedAmount
                                    value={invoiceTotals?.totalAdjustments ?? bill.totalAdjustments}
                                />
                            </dd>
                        </div>
                        <div>
                            <dt>Payments applied</dt>
                            <dd><MoneyAmount value={bill.totalPayments} className="" /></dd>
                        </div>
                        <div className="bill-run-detail__totals-due">
                            <dt>Amount due</dt>
                            <dd><MoneyAmount value={bill.totalDue} className="" /></dd>
                        </div>
                    </dl>
                    <p className="bill-run-detail__muted bill-run-detail__totals-note">
                        Amount due is the remaining balance from HRP after payments.
                    </p>
                </section>

                <footer className="bill-run-detail__footer">
                    <p>
                        This invoice is generated from HRP billing data for operational use.
                        Use ··· for Print / Save PDF, Download invoice pack
                        (CSV + printable HTML), or Send invoice (email draft).
                    </p>
                </footer>
            </article>

            <Drawer
                open={!!panel}
                onClose={closePanel}
                direction="right"
                size={isApplyPanel ? 480 : DRAWER_SIZE}
                duration={200}
                className={`bill-run-detail__drawer${
                    isApplyPanel ? " bill-run-detail__drawer--form" : ""
                }`}
                overlayClassName="bill-run-detail__drawer-overlay"
            >
                <div className="bill-run-detail__panel">
                    <header className="bill-run-detail__panel-head">
                        <div>
                            <p className="bill-run-detail__panel-kicker">{panelKicker}</p>
                            <h2>{panelTitle}</h2>
                        </div>
                        <button type="button" className="bill-run-detail__panel-close" onClick={closePanel}>
                            Close
                        </button>
                    </header>

                    <div className="bill-run-detail__panel-body">
                        {panel?.type === "apply-payment" && bill && !needsReview ? (
                            <ApplyPaymentPanel
                                key={`pay-${bill.key}`}
                                variant="drawer"
                                kind="premium"
                                context={{
                                    key: bill.key,
                                    billId: bill.key,
                                    accountKey: bill.accountKey,
                                    accountHccId: bill.accountHccId,
                                    accountName: bill.accountName,
                                    amountDue: bill.totalDue,
                                    statusLabel: bill.statusLabel || null,
                                    statusCode: bill.status || bill.statusCode || null,
                                    // Whole-bill apply: omit first-section plan/category. Pinning
                                    // CBC### from section[0] while paying the full invoice amount
                                    // makes HRP accept the batch but create no Premium Payment.
                                    billedPeriodStartDate:
                                        charges?.[0]?.billedPeriodStartDate || null,
                                    billedPeriodEndDate:
                                        charges?.[0]?.billedPeriodEndDate || null,
                                }}
                                onClose={closePanel}
                                onApplied={(result) =>
                                    refreshBillAfterPayment({
                                        queryClient,
                                        billFactKey,
                                        asOf,
                                        result,
                                        fetchDetail: fetchBillRunDetail,
                                    })
                                }
                            />
                        ) : null}

                        {panel?.type === "apply-adjustment" && bill && !needsReview ? (
                            <ApplyAdjustmentPanel
                                key={`adj-${bill.key}`}
                                variant="drawer"
                                kind="premium"
                                context={{
                                    key: bill.key,
                                    billId: bill.key,
                                    accountKey: bill.accountKey,
                                    accountHccId: bill.accountHccId,
                                    accountName: bill.accountName,
                                    billedPeriodStartDate:
                                        charges?.[0]?.billedPeriodStartDate || null,
                                    billedPeriodEndDate:
                                        charges?.[0]?.billedPeriodEndDate || null,
                                }}
                                onClose={closePanel}
                                onApplied={(result) =>
                                    refreshBillAfterAdjustment({
                                        queryClient,
                                        billFactKey,
                                        asOf,
                                        result,
                                        fetchDetail: fetchBillRunDetail,
                                    })
                                }
                            />
                        ) : null}

                        {panel?.type === "guide" && (
                            <div className="bill-run-detail__panel-money">
                                <section className="bill-run-detail__guide">
                                    <p className="bill-run-detail__guide-lead">
                                        This page is a <strong>premium invoice</strong>. Provider
                                        claims are billed separately on funding requests.
                                    </p>
                                    <div className="bill-run-detail__guide-grid">
                                        <article className="bill-run-detail__guide-card bill-run-detail__guide-card--active">
                                            <p className="bill-run-detail__guide-step">
                                                1 · This invoice
                                            </p>
                                            <h3>Premium bill</h3>
                                            <p>
                                                What the employer pays for the period: admin fees,
                                                stop-loss, and other invoice charges. Amount due
                                                on this page is real money owed.
                                            </p>
                                        </article>
                                        <article className="bill-run-detail__guide-card">
                                            <p className="bill-run-detail__guide-step">
                                                2 · On this invoice
                                            </p>
                                            <h3>Claims Funding (float)</h3>
                                            <p>
                                                Rate × enrollment (e.g. PSPM × subscribers) — cash
                                                set aside for claims. Billed like a fee, but it is
                                                <strong> not</strong> an admin processing fee and
                                                <strong> not</strong> a list of provider claims.
                                            </p>
                                        </article>
                                        <article className="bill-run-detail__guide-card">
                                            <p className="bill-run-detail__guide-step">
                                                3 · Separate document
                                            </p>
                                            <h3>Funding requests</h3>
                                            <p>
                                                When providers submit claims, those claim IDs
                                                (e.g. U2026…) show up here — not on this invoice.
                                            </p>
                                        </article>
                                    </div>
                                    <ol className="bill-run-detail__guide-flow">
                                        <li>Employer pays this premium invoice (including Claims Funding float).</li>
                                        <li>Provider claims arrive later.</li>
                                        <li>Those claims appear on a funding request cash call.</li>
                                    </ol>
                                    <Link
                                        to={fundingRequestsHref}
                                        className="btn bill-run-detail__btn bill-run-detail__guide-link"
                                        onClick={closePanel}
                                    >
                                        Open funding requests
                                        {bill.accountName || bill.accountHccId
                                            ? ` for ${bill.accountName || bill.accountHccId}`
                                            : ""}
                                    </Link>
                                </section>
                            </div>
                        )}

                        {panel?.type === "adjustment" && (
                            <dl className="bill-run-detail__panel-fields">
                                <DetailField label="Amount">
                                    <SignedAmount value={panel.data.amount} />
                                </DetailField>
                                <DetailField label="Reason">
                                    {panel.data.reason || "—"}
                                    {panel.data.reasonCode ? ` (${panel.data.reasonCode})` : ""}
                                </DetailField>
                                <DetailField label="Comment">{panel.data.comment || "—"}</DetailField>
                                <DetailField label="Category">{panel.data.categoryName || "—"}</DetailField>
                                <DetailField label="Plan">
                                    {panel.data.planName || "—"}
                                    {panel.data.planHccId ? (
                                        <div className="bill-run-detail__muted">{panel.data.planHccId}</div>
                                    ) : null}
                                </DetailField>
                                <DetailField label="Adjustment date">
                                    {panel.data.adjustmentDate || "—"}
                                </DetailField>
                                <DetailField label="Coverage date">
                                    {panel.data.coverageDate || "—"}
                                </DetailField>
                                <DetailField label="Billing period">
                                    {fmtPeriod(panel.data.periodStart, panel.data.periodEnd)}
                                </DetailField>
                                <DetailField label="Entered by">{panel.data.enteredBy || "—"}</DetailField>
                                <DetailField label="Transaction #">
                                    {panel.data.transactNum || "—"}
                                </DetailField>
                                <DetailField label="Status">
                                    {[
                                        panel.data.isReconciled ? "Reconciled" : "Open",
                                        panel.data.isStatistical ? "Statistical" : null,
                                    ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </DetailField>
                                <DetailField label="Adjustment ID">{panel.data.key}</DetailField>
                            </dl>
                        )}

                        {panel?.type === "retro" && (
                            <div className="bill-run-detail__panel-retro">
                                <dl className="bill-run-detail__panel-fields">
                                    <DetailField label="Category">
                                        {panel.data.categoryName || "—"}
                                    </DetailField>
                                    <DetailField label="Plan">
                                        {panel.data.planName || "—"}
                                        {panel.data.planHccId ? (
                                            <div className="bill-run-detail__muted">{panel.data.planHccId}</div>
                                        ) : null}
                                    </DetailField>
                                    <DetailField label="Period">
                                        {fmtPeriod(panel.data.periodStart, panel.data.periodEnd)}
                                    </DetailField>
                                    <DetailField label="Aggregate amount">
                                        <SignedAmount value={panel.data.amount} />
                                    </DetailField>
                                    <DetailField label="Lines">{panel.data.lineCount ?? "—"}</DetailField>
                                </dl>

                                <div className="bill-run-detail__panel-section">
                                    <div className="bill-run-detail__panel-section-head">
                                        <h3>Members</h3>
                                        {!!retroLines.length && (
                                            <span className="bill-run-detail__muted">
                                                {retroLines.length}
                                                {retroLinesTotal > retroLines.length
                                                    ? ` / ${retroLinesTotal}`
                                                    : ""}
                                                {" · "}
                                                <SignedAmount value={retroLinesAmount} />
                                            </span>
                                        )}
                                    </div>
                                    {retroLinesLoading && (
                                        <p className="bill-run-detail__muted">Loading member lines…</p>
                                    )}
                                    {retroLinesError && (
                                        <p className="bill-run-detail__panel-error">
                                            {retroLinesError?.message || "Unable to load member lines."}
                                        </p>
                                    )}
                                    {!retroLinesLoading && !retroLinesError && !retroLines.length && (
                                        <p className="bill-run-detail__muted">No member lines found.</p>
                                    )}
                                    {!!retroLines.length && (
                                        <>
                                            <div className="bill-run-detail__panel-colhead" aria-hidden="true">
                                                <span>Member</span>
                                                <span>Tier / method</span>
                                                <span className="num">Amount</span>
                                            </div>
                                            <div
                                                className="bill-run-detail__panel-table-scroll"
                                                ref={setRetroScrollRoot}
                                            >
                                                <table className="bill-run-detail__panel-table">
                                                    <tbody>
                                                        {retroLines.map((line) => (
                                                            <tr key={line.key}>
                                                                <td>
                                                                    <div className="bill-run-detail__method">
                                                                        {line.memberName || "—"}
                                                                    </div>
                                                                    <div className="bill-run-detail__muted">
                                                                        {line.memberHccId || "No member ID"}
                                                                    </div>
                                                                    {line.subscriptionHccId && (
                                                                        <div className="bill-run-detail__muted">
                                                                            Sub {line.subscriptionHccId}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div>{line.tierName || "—"}</div>
                                                                    <div className="bill-run-detail__muted">
                                                                        {[
                                                                            line.billingMethod,
                                                                            line.rateApplied != null
                                                                                ? `Rate ${money(line.rateApplied)}`
                                                                                : null,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(" · ") || "—"}
                                                                    </div>
                                                                </td>
                                                                <td className="num">
                                                                    <SignedAmount value={line.amount} />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                <div ref={retroLoadMoreRef} className="bill-run-detail__panel-sentinel">
                                                    {retroFetchingNext
                                                        ? "Loading more…"
                                                        : hasMoreRetroLines
                                                          ? "Scroll for more"
                                                          : "End of list"}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default BillRunDetail;
